import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@theraply/api-client';

// Types
export interface TherapistPublicProfile {
  id: number;
  firstName: string;
  lastName: string;
  bio?: string;
  profilePhotoS3Key?: string;
  specialties: string[];
  sessionTypes: string[];
  ratePerSession: number;
  rating: number;
  reviewCount: number;
  yearsOfExperience?: number;
  languages?: string[];
  isAcceptingNewClients: boolean;
  location?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
}

export interface Review {
  id: number;
  rating: number;
  text: string;
  createdAt: string;
  reviewerName: string;
}

export interface ReviewsResponse {
  reviews: Review[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AvailabilitySlot {
  date: string;
  startTime: string;
  endTime: string;
}

export interface AvailabilityPreviewResponse {
  therapistId: number;
  availableSlots: AvailabilitySlot[];
}

/**
 * Hook to fetch therapist public profile.
 *
 * Features:
 * - Fetches public profile data (no sensitive info)
 * - Caching via React Query
 * - Error handling
 *
 * @param therapistId - The therapist ID
 * @returns Query result with profile data
 */
export function useTherapistProfile(therapistId: number | undefined) {
  return useQuery<TherapistPublicProfile>({
    queryKey: ['therapist-profile', therapistId],
    queryFn: async () => {
      if (!therapistId) {
        throw new Error('Therapist ID is required');
      }
      const response = await apiClient.get(`/therapists/${therapistId}/public`);
      return response.data;
    },
    enabled: !!therapistId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1,
  });
}

/**
 * Hook to fetch therapist reviews with pagination.
 *
 * Features:
 * - Paginated reviews
 * - Caching per page
 * - Error handling
 *
 * @param therapistId - The therapist ID
 * @param page - Page number (default: 1)
 * @param limit - Results per page (default: 10)
 * @returns Query result with reviews and pagination info
 */
export function useTherapistReviews(
  therapistId: number | undefined,
  page: number = 1,
  limit: number = 10
) {
  return useQuery<ReviewsResponse>({
    queryKey: ['therapist-reviews', therapistId, page, limit],
    queryFn: async () => {
      if (!therapistId) {
        throw new Error('Therapist ID is required');
      }
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      const response = await apiClient.get(
        `/therapists/${therapistId}/reviews?${params.toString()}`
      );
      return response.data;
    },
    enabled: !!therapistId,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: 1,
  });
}

/**
 * Hook to fetch therapist availability preview (next 2 weeks).
 *
 * Features:
 * - Shows available slots for booking preview
 * - Caching
 * - Error handling
 *
 * @param therapistId - The therapist ID
 * @returns Query result with available slots
 */
export function useAvailabilityPreview(therapistId: number | undefined) {
  return useQuery<AvailabilityPreviewResponse>({
    queryKey: ['availability-preview', therapistId],
    queryFn: async () => {
      if (!therapistId) {
        throw new Error('Therapist ID is required');
      }
      const response = await apiClient.get(
        `/therapists/${therapistId}/availability-preview`
      );
      return response.data;
    },
    enabled: !!therapistId,
    staleTime: 60 * 1000, // Cache for 1 minute (availability changes frequently)
    gcTime: 2 * 60 * 1000, // Keep in cache for 2 minutes
    retry: 1,
  });
}
